import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { isEmpty, debounce } from 'lodash';
import { Grid } from 'antd';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { getSummaryScale } from '../../../../../core/utils/campaigns';

/**
 * SingleAxisBarChart (v2, Recharts)
 *
 * Drop-in replacement for the BizCharts SingleAxisBarChart used in Display
 * tab's "Device Type" view. Same props API: { data, leftY }.
 *
 * Sibling of DualAxisAreaChart — identical resize / responsive / tooltip
 * patterns so both charts behave the same inside the unified performance
 * card.
 */

const LEFT_FILL = '#b2d5ff';
const LEFT_STROKE = '#7dafff';

const labelToKey = (label) => {
  if (!label) return '';
  if (!label.includes(' ')) return label.toLowerCase();
  return `${label.charAt(0).toLowerCase()}${label.slice(1)}`.replace(/ /g, '');
};

const SingleAxisBarChart = ({ data, leftY }) => {
  const leftKey = labelToKey(leftY);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = screens.xs;
  const isTablet = !screens.xs && screens.md === false;
  const chartHeight = isMobile ? 260 : isTablet ? 320 : 400;

  const [resizeKey, setResizeKey] = useState(0);
  const handleResize = useCallback(
    debounce(() => setResizeKey((k) => k + 1), 150),
    []
  );
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const rows = useMemo(() => {
    if (isEmpty(data)) return [];
    return data.map((row) => ({
      xAxis: row.xAxis || 'Unknown',
      left: row[leftKey] != null ? Number(row[leftKey]) : 0,
    }));
  }, [data, leftKey]);

  const scale = useMemo(
    () => getSummaryScale(data, leftKey, null),
    [data, leftKey]
  );

  const leftFmt = scale && scale.left && scale.left.formatter
    ? scale.left.formatter
    : (v) => v;

  // Compact axis formatter — parity with DualAxisAreaChart. Prevents wide
  // aggregate numbers (1M+, 100K+) from being clipped inside the axis gutter.
  const compactAxis = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1)}B`;
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
    return leftFmt(n);
  };

  if (isEmpty(data)) return <div />;

  return (
    <div
      className="campaign-chart__responsive-container"
      style={{ width: '100%', height: chartHeight, position: 'relative', minWidth: 0 }}
      key={`${resizeKey}-${chartHeight}`}
    >
      <div style={{ position: 'absolute', inset: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
          >
            <defs>
              <linearGradient id="ds-singleaxis-bar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={LEFT_STROKE} stopOpacity={0.9} />
                <stop offset="100%" stopColor={LEFT_FILL} stopOpacity={0.7} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />

            <XAxis
              dataKey="xAxis"
              stroke="#94a3b8"
              fontSize={isMobile ? 10 : 12}
              tickMargin={8}
              axisLine={{ stroke: '#e8e8e8' }}
              tickLine={false}
            />

            <YAxis
              tickFormatter={compactAxis}
              stroke={LEFT_STROKE}
              fontSize={isMobile ? 10 : 12}
              tickMargin={8}
              axisLine={false}
              tickLine={false}
              width={64}
            />

            <Tooltip
              contentStyle={{
                background: '#fff',
                border: '1px solid #eeeeee',
                borderRadius: 12,
                boxShadow:
                  '0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.10)',
                fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                fontSize: 13,
                padding: 12,
              }}
              labelStyle={{
                color: '#111827',
                fontWeight: 600,
                marginBottom: 4,
                letterSpacing: '-0.01em',
              }}
              itemStyle={{ padding: 0 }}
              formatter={(value) => [leftFmt(value), leftY]}
              cursor={{ fill: 'rgba(203, 213, 225, 0.2)' }}
            />

            <Bar
              dataKey="left"
              name={leftY}
              fill="url(#ds-singleaxis-bar)"
              radius={[6, 6, 0, 0]}
              animationDuration={900}
              animationEasing="ease-out"
              isAnimationActive
            >
              {rows.map((_, idx) => (
                <Cell key={`cell-${idx}`} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SingleAxisBarChart;
